import type { Dispatch, PropsWithChildren, SetStateAction } from 'react';
import { createContext, useMemo, useState } from 'react';
import type {
  CustomerData,
  PaymentMetadata,
  PaymentProcessingChangeHandler,
  ProcessedPaymentInfo,
  ProductData
} from '@elfsight/lib-stripe-integration';
import { isPaymentCurrency } from '@elfsight/lib-stripe-integration';
import { useIsStripeConnected } from '@elfsight/lib-stripe-integration/widget';
import { useContextOrThrow } from '@elfsight/lib-utils';
import { useFormSelector } from '@elfsight/widget-form-builder/widget';
import { noop } from 'es-toolkit';
import { useServices } from '../service/services-provider';
import { getProductData } from './get-product-data';
import { usePaymentMetadata } from './use-payment-metadata';

type BookingStep = 'form' | 'payment';

type PaymentDataContextType = {
  canProceed: boolean;
  currentStep: BookingStep;
  customerData: CustomerData;
  setCurrentStepToForm: () => void;
  setCurrentStepToPayment: () => void;
  productData?: ProductData;
  paymentMetadata?: PaymentMetadata;
  shouldUsePayment: boolean;
  processedPaymentInfo: ProcessedPaymentInfo;
  setProcessedPaymentInfo: Dispatch<SetStateAction<ProcessedPaymentInfo>>;
  isPaymentInProcess: boolean;
  handlePaymentProcessingChange: PaymentProcessingChangeHandler;
};

const PaymentDataContext = createContext<PaymentDataContextType>({
  canProceed: false,
  currentStep: 'form',
  customerData: { email: '' },
  setCurrentStepToForm: noop,
  setCurrentStepToPayment: noop,
  productData: undefined,
  paymentMetadata: undefined,
  shouldUsePayment: false,
  processedPaymentInfo: undefined,
  setProcessedPaymentInfo: noop,
  isPaymentInProcess: false,
  handlePaymentProcessingChange: noop
});

export function PaymentDataProvider({ children }: PropsWithChildren) {
  const { currentService } = useServices();
  const {
    fields: { email, name },
    fieldsError
  } = useFormSelector();
  const isStripeConnected = useIsStripeConnected();
  const paymentMetadata = usePaymentMetadata();

  const [currentStep, setCurrentStep] = useState<BookingStep>('form');
  const [isPaymentInProcess, setIsPaymentInProcess] = useState(false);
  const [processedPaymentInfo, setProcessedPaymentInfo] =
    useState<ProcessedPaymentInfo>();

  const providerValue = useMemo(() => {
    const productData = getProductData(currentService);
    const customerData: CustomerData = {
      email: (email?.value ?? '').toString(),
      name: name?.value?.toString()
    };

    const isDataValid = !!(productData && customerData.email && email.valid);

    const shouldUsePayment =
      isStripeConnected &&
      currentService?.price.type === 'fixed' &&
      isPaymentCurrency(productData?.currency);

    const handlePaymentProcessingChange = (isInProcess: boolean) => {
      setIsPaymentInProcess(isInProcess);
    };

    return {
      canProceed: isDataValid && !fieldsError.invalid && !fieldsError.required,
      currentStep,
      customerData,
      setCurrentStepToForm: () => setCurrentStep('form'),
      setCurrentStepToPayment: () => setCurrentStep('payment'),
      productData,
      paymentMetadata,
      shouldUsePayment,
      processedPaymentInfo,
      setProcessedPaymentInfo,
      isPaymentInProcess,
      handlePaymentProcessingChange
    };
  }, [
    currentService,
    currentStep,
    paymentMetadata,
    isStripeConnected,
    isPaymentInProcess,
    fieldsError,
    processedPaymentInfo,
    email,
    name
  ]);

  return (
    <PaymentDataContext.Provider value={providerValue}>
      {children}
    </PaymentDataContext.Provider>
  );
}

export function usePaymentData() {
  return useContextOrThrow(PaymentDataContext, 'PaymentData');
}
